import React, { useCallback, useEffect, useState, useRef } from "react";
import { Box, MenuItem, TextField } from "@mui/material";
import axios from "axios";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { fetchEmployeeExit } from "../../../Apis/Employee-api";
import LogoutIcon from "@mui/icons-material/Logout";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  EMPLOYEE_EXIT_DEFAULT_COLUMNS,
  EMPLOYEE_EXITS_DATAGRID_KEY,
  mapSavedEmployeeExitColumns,
} from "./employeeExitListConstants";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "employee_exit_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  // Global search sent as request body "search" so backend searches all columns
  return filters;
};

function EmployeeExitList() {
  const [exit, setexit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(EMPLOYEE_EXIT_DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exitReasonOptions, setExitReasonOptions] = useState([]);
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  // Load personalized layout (same API/key as Personalize View)
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const datagridKey = `${EMPLOYEE_EXITS_DATAGRID_KEY}_${org.organization_id}`;
        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: org.organization_id,
          }),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg?.columns?.length > 0) {
            setTableConfig(serverCfg);
            setConfigColumns(mapSavedEmployeeExitColumns(serverCfg.columns));
          } else {
            setTableConfig(null);
            setConfigColumns(EMPLOYEE_EXIT_DEFAULT_COLUMNS);
          }
        } else {
          setTableConfig(null);
          setConfigColumns(EMPLOYEE_EXIT_DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(EMPLOYEE_EXIT_DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // load EmployeeExit data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const exitReasonId = String(
          searchParams.get("organization_employment_exit_reason_id") || ""
        ).trim();
        const requestBody = {
          filters,
          ...(exitReasonId && exitReasonId.toLowerCase() !== "all"
            ? { organization_employment_exit_reason_id: exitReasonId }
            : {}),
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "employee_exit_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employee-exits`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchEmployeeExit(org.organization_id);
          data = oldData?.employeexit?.data || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const rows = (Array.isArray(data) ? data : []).map((item) => ({
          ...item,
          id: item.employee_exit_id,
          employee_name: item.employee_name || item.full_name || item.name || "-",
          exit_interview_done:
            item.exit_interview_done === true ||
            item.exit_interview_done === 1 ||
            item.exit_interview_done === "1"
              ? "Yes"
              : item.exit_interview_done === false ||
                  item.exit_interview_done === 0 ||
                  item.exit_interview_done === "0"
                ? "No"
                : item.exit_interview_done ?? "-",
        }));
        setexit(rows);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch employee exits:", err);
          toast.error("Failed to fetch employee exits");
          setexit([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  const deleteExit = useCallback(
    async (id, options = {}) => {
      try {
        const restoreEmployee = options.restore_employee ? 1 : 0;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/employee-exit/${id}?restore_employee=${restoreEmployee}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Employee exit deleted successfully");
          // Refresh data after deletion by triggering useEffect
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorMessage =
            data.message || data.error || "Failed to delete employee exit";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete employee exit"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/employee/employee-exits/edit/${item.id || item.employee_exit_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization/employee/employee-exits/view/${item.id || item.employee_exit_id}`
      );
    },
    [navigate]
  );

  useEffect(() => {
    if (!org?.organization_id) {
      setExitReasonOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employemnt-exit-reason`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            params: { per_page: "all" },
          }
        );
        if (cancelled) return;
        const payload = response.data?.exitreason;
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        const options = rows
          .map((item) => ({
            id: item?.organization_employment_exit_reason_id ?? item?.id,
            name: String(item?.employment_exit_reason_name || "").trim(),
          }))
          .filter((item) => item.id != null && item.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setExitReasonOptions(options);
      } catch (err) {
        console.error("Error fetching exit reasons:", err);
        if (!cancelled) setExitReasonOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  const selectedExitReason = (() => {
    const raw = String(
      searchParams.get("organization_employment_exit_reason_id") || ""
    ).trim();
    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
  })();
  const handleExitReasonFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== "All") {
        next.set("organization_employment_exit_reason_id", value);
      } else {
        next.delete("organization_employment_exit_reason_id");
      }
      next.set("page", "1");
      return next;
    });
  };
  const exitReasonToolbar = (
    <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
      <TextField
        select
        size="small"
        label="Exit Reason"
        value={selectedExitReason}
        onChange={handleExitReasonFilterChange}
        sx={{
          width: 280,
          minWidth: 280,
          "& .MuiSelect-select": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      >
        <MenuItem value="All">All</MenuItem>
        {exitReasonOptions.map((reason) => (
          <MenuItem key={String(reason.id)} value={String(reason.id)}>
            {reason.name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        add_action={"EMPLOYEE_EXIT_ADD"}
        heading={"Employee Exit"}
        btnName={"Add Employee Exit"}
        delete_action={"EMPLOYEE_EXIT_DELETE"}
        Data={exit}
        tableHeaders={[
          { name: "Code", value_key: "employee_code", width: "50px" },
          { name: "Name", value_key: "employee_name", width: "150px" },

          {
            name: "NoticePeriod Starts",
            value_key: "notice_period_start",
            width: "170px",
          },

          {
            name: "NoticePeriod Ends",
            value_key: "notice_period_end",
            width: "170px",
          },
          {
            name: "Resignation Date",
            value_key: "resignation_date",
            width: "150px",
          },
          {
            name: "Relieving Date",
            value_key: "relieving_date",
            width: "150px",
          },
          {
            name: "Interview",
            value_key: "exit_interview_done",
            width: "50px",
          },
        ]}
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <AlarmAddIcon sx={{ color: "text.secondary" }} />,
          <LogoutIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Employee exit details",
          "Employee exit details",
          "Add Employee Exit Details",
          "Employee Exit Deatils",
        ]}
        Route={"/organization/employee/employee-exits"}
        setData={setexit}
        DeleteFunc={deleteExit}
      />
      <div className="ems-section-divider" />
      {!loadingConfig && (
        <TableDataGeneric
          tableName="employee_exits"
          primaryKey="employee_exit_id"
          heading="Employee Exit"
          data={exit}
          sortname="employee_name"
          showActions={true}
          edit_delete_action={["EMPLOYEE_EXIT_EDIT", "EMPLOYEE_EXIT_DELETE"]}
          Route="/organization/employee/employee-exits"
          DeleteFunc={deleteExit}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="employee_exits"
          loadedBackendConfig={tableConfig}
          loading={loading || loadingConfig}
          toolbarCenterContent={exitReasonToolbar}
          deleteRestoreOption={true}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default EmployeeExitList;

